/**
 * 69.29 step 6 (ARCHITECTURE §5.25 (d); DECISIONS 245, the owner: "yep let's do bot v tank"), with GameConfig.vehicleZones: a
 * rocket at an armoured hull goes at the face of it turned toward the shooter — its side or its tail when one is offered,
 * not its middle — and a face its round would barely scratch is not worth the rocket. Each face of the hull's box (its hit
 * shape's, in its frame: core/vehicleFrame.ts), turned toward the eye, takes the zone its centre lies in (core/vehicleZones.ts
 * zoneAt: the table's rating) at the angle the flight meets it: core/vehicleZones.ts angledRating, the damage model's rule — for the armoured and heavy
 * classes (§5.25 (b): a glancing hit on the front fails, a square one on the side gets through; the soft and light classes
 * square, their ratings under every penetration that matters). DECISIONS 246 (the damage cumulative): each face's expected
 * share of the blow (core/vehicleZones.ts expectedShare, the hits' wear by the margin) ranks them; the best with a clear line is
 * the aim; none over EXPECTED_HOLD (only a front too thick at that angle offered): no rocket. Pure.
 */
import type { GameState, StructureState, Vec3 } from '../types';
import type { ExplosionClass } from '../constants/explosions';
import { BURST_PENETRATION, type ZoneTable } from '../constants/vehicleZones';
import { HITBOXES } from '../constants/hitboxes';
import type { BodyKey } from '../constants/body';
import { angledRating, EXPECTED_HOLD, expectedShare, zoneAt, zoneTableOf, type Local } from '../core/vehicleZones';
export { angledRating, ANGLE_FLOOR } from '../core/vehicleZones';   // (the damage model's own: one rule for the hits and the bots' aim)
import { vehicleFrame } from '../core/vehicleFrame';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';


export type HullFace = 'front' | 'rear' | 'left' | 'right' | 'top';
export interface FaceAim { face: HullFace; point: Vec3; rating: number; effective: number; cos: number; /** 69.29 step 6 (246): the share of a round's blow it is expected to take (core/vehicleZones.ts expectedShare), for the `pen` asked */ expected: number }

/** What a burst of `cls` brings against a zone (BURST_PENETRATION). */
export function burstPenetration(cls: ExplosionClass): number { return BURST_PENETRATION[cls] ?? 0; }

/** The hull box of a body's hit shape (its first box: the hull, not a turret), in its frame (people). */
function hullBox(type: StructureState['type']): { lo: Local; hi: Local } | null {
  const part = HITBOXES[type as BodyKey]?.parts.find(p => p.shape === 'box');
  if (!part || part.shape !== 'box') return null;
  const c = part.centre, h = part.half;
  return { lo: [c[0] - h[0], c[1] - h[1], c[2] - h[2]], hi: [c[0] + h[0], c[1] + h[1], c[2] + h[2]] };
}

/**
 * The faces of hull `v` a shooter at `eye` sees turned toward it, each at its centre, with its zone's rating and its
 * effective rating at that angle and the share of a round of `pen`'s blow it is expected to take — best first (the most
 * expected), `table` (absent: the body's own).
 */
export function hullFaces(state: Pick<GameState, 'planets' | 'config'>, v: Pick<StructureState, 'type' | 'surfaceDir' | 'drive' | 'planetIndex'>, eye: Vec3, table: ZoneTable | null = zoneTableOf(v), pen = 0): FaceAim[] {
  const box = hullBox(v.type), planet = state.planets[v.planetIndex];
  if (!box || !table || !planet) return [];
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const f = vehicleFrame(planet, v, person, { fineAmplitude: ps.fineAmplitude, fineWavelength: ps.fineWavelength, k: ps.k, materialRelief: ps.materialRelief });
  const { lo, hi } = box, mid: Local = [(lo[0] + hi[0]) / 2, (lo[1] + hi[1]) / 2, (lo[2] + hi[2]) / 2];
  const world = (p: Local) => vecAdd(f.foot, vecAdd(vecScale(f.left, p[0] * person), vecAdd(vecScale(f.up, p[1] * person), vecScale(f.front, p[2] * person))));
  const faces: Array<{ face: HullFace; at: Local; normal: Vec3 }> = [
    { face: 'front', at: [mid[0], mid[1], hi[2]], normal: f.front },
    { face: 'rear', at: [mid[0], mid[1], lo[2]], normal: vecScale(f.front, -1) },
    { face: 'left', at: [hi[0], mid[1], mid[2]], normal: f.left },
    { face: 'right', at: [lo[0], mid[1], mid[2]], normal: vecScale(f.left, -1) },
    { face: 'top', at: [mid[0], hi[1], mid[2]], normal: f.up },
  ];
  const out: FaceAim[] = [];
  for (const x of faces) {
    const point = world(x.at), to = vecSub(point, eye), len = vecLength(to);
    if (len < 1e-9) continue;
    const cos = -vecDot(vecScale(to, 1 / len), vecNormalize(x.normal));   // (the flight's meeting the face: its normal against the flight)
    if (cos <= 0) continue;   // (turned away: not this side of the hull)
    const rating = table.rating[zoneAt(table, x.at)];
    out.push({ face: x.face, point, rating, effective: angledRating(table.cls, rating, cos), cos, expected: expectedShare(table.cls, pen, rating, cos) });
  }
  return out.sort((a, b) => b.expected - a.expected || a.effective - b.effective || b.cos - a.cos);
}

/**
 * 69.29 step 6 (DECISIONS 246: "fire at the face with the most expected damage … hold only when the expected damage is
 * negligible"): the face of hull `v` a round of penetration `pen` from `eye` is expected to hurt the most — its share of the
 * blow (core/vehicleZones.ts expectedShare: the hits' wear on its angled rating) — whose line `clear` passes, over EXPECTED_HOLD;
 * or null: every face it sees too thick to matter (a tank's front, a side well off square), or no clear line.
 */
export function rocketFace(state: Pick<GameState, 'planets' | 'config'>, v: Pick<StructureState, 'type' | 'surfaceDir' | 'drive' | 'planetIndex'>, eye: Vec3, pen: number, clear?: (point: Vec3) => boolean, table?: ZoneTable | null): FaceAim | null {
  for (const x of hullFaces(state, v, eye, table === undefined ? zoneTableOf(v) : table, pen)) {
    if (x.expected <= EXPECTED_HOLD) break;   // (best first: none after it either)
    if (!clear || clear(x.point)) return x;
  }
  return null;
}
